import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Grid,
  Divider,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CloudDownload as CloudDownloadIcon,
  Preview as PreviewIcon,
  Analytics as AnalyticsIcon,
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { snowflakeApi } from '../../utils/api';

interface UnifiedSamplingDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  workflowId?: string;
  onKeywordsLoaded: (keywords: string[]) => void;
}

interface SamplingResponse {
  success: boolean;
  keywords: Array<{
    keyword: string;
    impression?: number;
    category?: string;
    subtopic?: string;
    parent_topic?: string;
    search_volume?: number;
    commercial_score?: number;
    specificity_score?: number;
  }>;
  metadata: {
    source: string;
    table: string;
    strategy: string;
    total_available: number;
    sample_size: number;
    requested_size: number;
    is_preview: boolean;
  };
  statistics?: {
    impression_stats: {
      high: number;
      medium: number;
      low: number;
      average: number;
      median: number;
      max: number;
      min: number;
    };
    category_distribution?: Record<string, number>;
    topic_distribution?: Record<string, number>;
  };
  fallback_info?: {
    triggered: boolean;
    reason: string;
    original_source: string;
    original_strategy: string;
    adjusted_source: string;
    adjusted_strategy: string;
  };
  error?: string;
}

const DATA_SOURCES = [
  {
    value: 'serp_sampling',
    label: 'SERP Sampling Table',
    description: 'Comprehensive table with SERP data, categories, and topics',
    table: 'TOTALSERP.SERP_SAMPLING_TABLE',
    recommended: true,
  },
  {
    value: 'distinct_list',
    label: 'Distinct List Table',
    description: 'Simplified table with unique keywords and impressions',
    table: 'TOTALSERP.DISTINCT_LIST',
    recommended: false,
  },
  {
    value: 'custom',
    label: 'Custom SQL Query',
    description: 'Write your own SQL for advanced sampling control',
    table: 'User-specified',
    recommended: false,
  },
];

const SAMPLING_STRATEGIES = {
  serp_sampling: [
    {
      value: 'stratified',
      label: 'Stratified by Categories',
      description: 'Sample proportionally from PRESENT categories',
      icon: <CategoryIcon />,
    },
    {
      value: 'stratified_v2',
      label: 'Stratified by Topics',
      description: 'Sample from diverse topic combinations',
      icon: <CategoryIcon />,
    },
    {
      value: 'representative',
      label: 'Representative Distribution',
      description: 'Balanced sampling across impression ranges',
      icon: <AnalyticsIcon />,
    },
    {
      value: 'high_volume',
      label: 'High Volume Priority',
      description: 'Focus on highest impression keywords',
      icon: <TrendingUpIcon />,
    },
  ],
  distinct_list: [
    {
      value: 'representative',
      label: 'Representative Distribution',
      description: 'Balanced sampling across impression ranges',
      icon: <AnalyticsIcon />,
    },
    {
      value: 'high_volume',
      label: 'High Volume Priority',
      description: 'Focus on highest impression keywords',
      icon: <TrendingUpIcon />,
    },
  ],
  custom: [
    {
      value: 'custom_sql',
      label: 'Custom SQL Query',
      description: 'Execute your custom sampling logic',
      icon: <CodeIcon />,
    },
  ],
};

const UnifiedSamplingDialog: React.FC<UnifiedSamplingDialogProps> = ({
  open,
  onClose,
  clientId,
  workflowId,
  onKeywordsLoaded,
}) => {
  const [source, setSource] = useState<string>('serp_sampling');
  const [strategy, setStrategy] = useState<string>('stratified');
  const [sampleSize, setSampleSize] = useState(100);
  const [minImpressions, setMinImpressions] = useState<number | ''>('');
  const [maxImpressions, setMaxImpressions] = useState<number | ''>('');
  const [customQuery, setCustomQuery] = useState('');
  const [customParameters, setCustomParameters] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState<SamplingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hasAdjusted, setHasAdjusted] = useState(false);

  // Reset strategy when source changes
  useEffect(() => {
    if (source && SAMPLING_STRATEGIES[source as keyof typeof SAMPLING_STRATEGIES]) {
      // Only reset strategy if user manually changed source (not from adjustment)
      if (!hasAdjusted) {
        setStrategy(SAMPLING_STRATEGIES[source as keyof typeof SAMPLING_STRATEGIES][0].value);
      }
    }
    // Clear preview when source changes manually
    if (!hasAdjusted) {
      setPreviewData(null);
      setError(null);
    }
    setHasAdjusted(false);
  }, [source]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      // Reset to defaults when dialog opens
      setSource('serp_sampling');
      setStrategy('stratified');
      setHasAdjusted(false);
      setPreviewData(null);
      setError(null);
    }
  }, [open]);
  
  // Auto-load preview when settings change
  useEffect(() => {
    // Validate that the strategy is valid for the current source
    const availableStrategies = SAMPLING_STRATEGIES[source as keyof typeof SAMPLING_STRATEGIES] || [];
    const isValidStrategy = availableStrategies.some(s => s.value === strategy);
    
    if (open && source !== 'custom' && !hasAdjusted && isValidStrategy) {
      loadPreview();
    }
  }, [open, source, strategy, sampleSize, minImpressions, maxImpressions]);

  const loadPreview = async () => {
    if (source === 'custom' && !customQuery) return;
    
    setLoadingPreview(true);
    try {
      const response = await snowflakeApi.post<SamplingResponse>('/api/v1/keywords/unified-sample', {
        source,
        strategy: source === 'custom' ? 'custom_sql' : strategy,
        client_id: clientId,
        workflow_id: workflowId,
        sample_size: sampleSize,
        min_impressions: minImpressions || undefined,
        max_impressions: maxImpressions || undefined,
        custom_query: source === 'custom' ? customQuery : undefined,
        custom_parameters: source === 'custom' ? customParameters : undefined,
        preview: true,
      });
      
      if (response.data.success) {
        setPreviewData(response.data);
        setError(null);
        
        // If there was an automatic adjustment, update the UI to reflect it
        if (response.data.fallback_info?.triggered) {
          setHasAdjusted(true);
          setSource(response.data.fallback_info.adjusted_source);
          setStrategy(response.data.fallback_info.adjusted_strategy);
        }
      } else {
        setError(response.data.error || 'Failed to load preview');
      }
    } catch (err: any) {
      console.error('Preview error:', err);
      // Don't set error for preview failures, just clear the preview
      setPreviewData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleLoadKeywords = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await snowflakeApi.post<SamplingResponse>('/api/v1/keywords/unified-sample', {
        source,
        strategy: source === 'custom' ? 'custom_sql' : strategy,
        client_id: clientId,
        workflow_id: workflowId,
        sample_size: sampleSize,
        min_impressions: minImpressions || undefined,
        max_impressions: maxImpressions || undefined,
        custom_query: source === 'custom' ? customQuery : undefined,
        custom_parameters: source === 'custom' ? customParameters : undefined,
        preview: false,
      });
      
      if (response.data.success) {
        const keywords = response.data.keywords.map(k => k.keyword);
        onKeywordsLoaded(keywords);
        onClose();
      } else {
        setError(response.data.error || 'Failed to load keywords');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load keywords');
    } finally {
      setLoading(false);
    }
  };

  const parseQueryParameters = (query: string): string[] => {
    if (!query) return [];
    const parameterMatches = query.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || [];
    const parameters = parameterMatches.map(match => match.substring(1));
    const systemParameters = ['client_id', 'sample_size', 'min_impressions', 'max_impressions'];
    return [...new Set(parameters)].filter(param => !systemParameters.includes(param));
  };

  const handleCustomQueryChange = (query: string) => {
    setCustomQuery(query);
    const customParams = parseQueryParameters(query);
    const newParameterValues: Record<string, string> = {};
    customParams.forEach(param => {
      newParameterValues[param] = customParameters[param] || '';
    });
    setCustomParameters(newParameterValues);
  };

  const selectedSource = DATA_SOURCES.find(s => s.value === source);
  const availableStrategies = SAMPLING_STRATEGIES[source as keyof typeof SAMPLING_STRATEGIES] || [];
  const selectedStrategy = availableStrategies.find(s => s.value === strategy);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <CloudDownloadIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Unified Keyword Sampling</Typography>
          </Box>
          {previewData && (
            <Chip
              label={`${previewData.metadata.total_available.toLocaleString()} keywords available`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Fallback notification */}
          {previewData && previewData.fallback_info?.triggered && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <AlertTitle>Automatic Data Source Adjustment</AlertTitle>
                <Typography variant="body2" gutterBottom>
                  {previewData.fallback_info.reason}
                </Typography>
                <Typography variant="body2">
                  • Original selection: <strong>{previewData.fallback_info.original_source}</strong> with <strong>{previewData.fallback_info.original_strategy}</strong> strategy
                </Typography>
                <Typography variant="body2">
                  • Automatically adjusted to: <strong>{previewData.fallback_info.adjusted_source}</strong> with <strong>{previewData.fallback_info.adjusted_strategy}</strong> strategy
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  This adjustment ensures you receive actual keyword data. You can manually select a different source if needed.
                </Typography>
              </Alert>
            </Grid>
          )}
          
          {/* Data Source Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <StorageIcon sx={{ mr: 1 }} />
              Step 1: Select Data Source
            </Typography>
            <Grid container spacing={2}>
              {DATA_SOURCES.map((ds) => (
                <Grid item xs={12} md={4} key={ds.value}>
                  <Paper
                    variant={source === ds.value ? 'elevation' : 'outlined'}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: source === ds.value ? '2px solid' : '1px solid',
                      borderColor: source === ds.value ? 'primary.main' : 'divider',
                      position: 'relative',
                      height: '60px',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => { 
                      setSource(ds.value); 
                      setHasAdjusted(false);
                      // Immediately set the first valid strategy for this source
                      const strategies = SAMPLING_STRATEGIES[ds.value as keyof typeof SAMPLING_STRATEGIES];
                      if (strategies && strategies.length > 0) {
                        setStrategy(strategies[0].value);
                      }
                    }}
                  >
                    {ds.recommended && (
                      <Chip
                        label="Recommended"
                        size="small"
                        color="success"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    )}
                    {previewData?.fallback_info?.triggered && source === ds.value && ds.value === previewData.fallback_info.adjusted_source && (
                      <Chip
                        label="Auto-selected"
                        size="small"
                        color="warning"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    )}
                    <Typography variant="subtitle2" gutterBottom>
                      {ds.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {ds.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Strategy Selection */}
          {source !== 'custom' && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AnalyticsIcon sx={{ mr: 1 }} />
                Step 2: Select Sampling Strategy
              </Typography>
              <Grid container spacing={2}>
                {availableStrategies.map((strat) => (
                  <Grid item xs={12} md={6} key={strat.value}>
                    <Paper
                      variant={strategy === strat.value ? 'elevation' : 'outlined'}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: strategy === strat.value ? '2px solid' : '1px solid',
                        borderColor: strategy === strat.value ? 'primary.main' : 'divider',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => { setStrategy(strat.value); setHasAdjusted(false); }}
                    >
                      {previewData?.fallback_info?.triggered && strategy === strat.value && strat.value === previewData.fallback_info.adjusted_strategy && (
                        <Chip
                          label="Auto-selected"
                          size="small"
                          color="warning"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                      )}
                      <Box display="flex" alignItems="center" mb={1}>
                        {strat.icon}
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                          {strat.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {strat.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Custom Query */}
          {source === 'custom' && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CodeIcon sx={{ mr: 1 }} />
                Step 2: Write Custom SQL Query
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Query Guidelines</AlertTitle>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>Your query must SELECT a column aliased as KEYWORD</li>
                  <li>Use :client_id parameter for client filtering</li>
                  <li>Use :sample_size parameter for result limiting</li>
                  <li>Custom parameters (like :keyword_pattern) will create input fields</li>
                </ul>
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={customQuery}
                onChange={(e) => handleCustomQueryChange(e.target.value)}
                placeholder={`SELECT 
  QUERY as KEYWORD,
  IMPRESSION
FROM TOTALSERP.SERP_SAMPLING_TABLE
WHERE CLIENT_ID = :client_id
  AND WORKFLOW_ID = :workflow_id
  AND IMPRESSION >= :min_impressions
ORDER BY IMPRESSION DESC
LIMIT :sample_size`}
                sx={{
                  fontFamily: 'monospace',
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
              
              {/* Custom Parameters */}
              {Object.keys(customParameters).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Query Parameters
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(customParameters).map(([paramName, paramValue]) => (
                      <Grid item xs={12} md={6} key={paramName}>
                        <TextField
                          fullWidth
                          label={`:${paramName}`}
                          value={paramValue}
                          onChange={(e) =>
                            setCustomParameters((prev) => ({
                              ...prev,
                              [paramName]: e.target.value,
                            }))
                          }
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>
          )}

          {/* Configuration */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Step 3: Configure Sampling
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sample Size"
                  value={sampleSize}
                  onChange={(e) => setSampleSize(Number(e.target.value))}
                  inputProps={{ min: 10, max: 1000 }}
                  helperText="Number of keywords to sample"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Impressions (Optional)"
                  value={minImpressions}
                  onChange={(e) => setMinImpressions(e.target.value ? Number(e.target.value) : '')}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Impressions (Optional)"
                  value={maxImpressions}
                  onChange={(e) => setMaxImpressions(e.target.value ? Number(e.target.value) : '')}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Preview Section */}
          {(previewData || loadingPreview) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PreviewIcon sx={{ mr: 1 }} />
                Preview Results
                {loadingPreview && (
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                )}
              </Typography>
              
              {loadingPreview && !previewData && (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              )}
              
              {/* Statistics */}
              {previewData && previewData.statistics && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {previewData.metadata.total_available.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Total Available</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">
                        {Math.round(previewData.statistics.impression_stats.average).toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Avg Impressions</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Chip label={`H: ${previewData.statistics.impression_stats.high}`} size="small" color="success" />
                        <Chip label={`M: ${previewData.statistics.impression_stats.medium}`} size="small" color="warning" />
                        <Chip label={`L: ${previewData.statistics.impression_stats.low}`} size="small" />
                      </Stack>
                      <Typography variant="caption">Distribution</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">
                        {previewData.metadata.table === 'custom' ? 'Custom' : selectedSource?.label}
                      </Typography>
                      <Typography variant="caption">Data Source</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Sample Keywords */}
              {previewData && previewData.keywords && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Sample Keywords ({previewData.keywords.length} shown)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Keyword</TableCell>
                            {previewData.keywords[0]?.category && <TableCell>Category</TableCell>}
                            {previewData.keywords[0]?.subtopic && <TableCell>Subtopic</TableCell>}
                            <TableCell align="right">Impressions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.keywords.slice(0, 10).map((kw, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{kw.keyword}</TableCell>
                            {kw.category && (
                              <TableCell>
                                <Chip label={kw.category} size="small" variant="outlined" />
                              </TableCell>
                            )}
                            {kw.subtopic && (
                              <TableCell>
                                <Typography variant="caption">{kw.subtopic}</Typography>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {(kw.impression || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
            </Grid>
          )}

          {/* Error Display */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={loadPreview}
          variant="outlined"
          startIcon={loadingPreview ? <CircularProgress size={20} /> : <PreviewIcon />}
          disabled={loading || loadingPreview || (source === 'custom' && !customQuery)}
        >
          {loadingPreview ? 'Loading Preview...' : (source === 'custom' ? 'Preview Query' : 'Refresh Preview')}
        </Button>
        <Button
          onClick={handleLoadKeywords}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          disabled={
            loading ||
            (source === 'custom' && !customQuery) ||
            (source === 'custom' && Object.values(customParameters).some((v) => !v))
          }
        >
          {loading ? 'Loading...' : `Load ${sampleSize} Keywords`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedSamplingDialog;